import {view, css, Action, Icon, Tooltip} from '@sail/ui/deprecated';
import type {IconAsset} from '@sail/icons/types';
import {MessageDescriptor, useIntl} from 'react-intl';
import {useCallback, useMemo, useRef} from 'react';
import {useTheme} from '../../ThemeProvider';

export type Placement = React.ComponentProps<typeof Tooltip>['placement'];

interface Props {
  icon: IconAsset;
  rotateIcon?: boolean;
  color?: string;
  hoverColor?: string;
  label: MessageDescriptor | string;
  onPress?: () => void;
  testId?: string;
  tooltipPlacement?: Placement;
  tooltipEnabled?: boolean;
  disabled?: boolean;
}

export function HeaderButton({
  icon,
  rotateIcon,
  color,
  hoverColor,
  label,
  onPress,
  testId,
  tooltipPlacement,
  tooltipEnabled = true,
  disabled = false,
}: Props) {
  const intl = useIntl();
  const btnRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();

  const iconColor = color || theme.onBrandTextColor;

  const handleOnPress = useCallback(() => {
    if (btnRef.current) {
      btnRef.current.children[0]?.dispatchEvent(new Event('blur'));
    }

    if (onPress) {
      onPress();
    }
  }, [onPress]);

  const innerIcon = useMemo(
    () => (
      <Icon
        icon={icon}
        size="xsmall"
        tabIndex={disabled ? -1 : 0}
        css={{
          padding: 'xsmall',
          // legacy sail uses border-box; setting explicitly so legacy doesn't break this
          boxSizing: css.value('content-box'),
          cursor: css.value('pointer'),
          color: css.value(iconColor),
          backgroundColor: css.value(theme.brandColor),
          borderRadius: 'xsmall',
          verticalAlign: css.value('middle'),
          '&:hover': {
            backgroundColor: css.value(theme.ctaHoverColor),
            color: hoverColor ? css.value(hoverColor) : undefined,
          },
          transform: rotateIcon ? css.value('rotate(90deg)') : undefined,
          '&:focus': {
            outline: theme.focusVisibleOutline,
            focusRing: theme.focusVisibleOutline
              ? css.value('none')
              : undefined,
          },
        }}
      />
    ),
    [
      icon,
      iconColor,
      hoverColor,
      theme.brandColor,
      theme.ctaHoverColor,
      theme.focusVisibleOutline,
      rotateIcon,
      disabled,
    ],
  );

  const localizedLabel =
    typeof label === 'string' ? label : intl.formatMessage(label);

  return (
    <Action
      css={{
        height: css.value('100%'),
        display: css.value('flex'),
        focusRing: css.value('none'),
      }}
      tabIndex={-1}
      onPress={handleOnPress}
      disabled={disabled}
    >
      <view.button
        data-testid={testId}
        ref={btnRef}
        tabIndex={-1}
        css={{
          background: css.value('none'),
          border: css.value('none'),
          padding: css.value('none'),
          margin: css.value('none'),
        }}
        aria-label={localizedLabel}
        disabled={disabled}
      >
        {label && tooltipEnabled && !disabled ? (
          <Tooltip
            trigger={innerIcon}
            type="label"
            placement={tooltipPlacement}
            key={
              tooltipPlacement ? tooltipPlacement.toString() : label.toString()
            }
          >
            {localizedLabel}
          </Tooltip>
        ) : (
          innerIcon
        )}
      </view.button>
    </Action>
  );
}
